import { useMemo } from "react";
import { Empty, GridNote, Panel, SearchBox, Select, SourceError, Toggle, WalletLink } from "../components/Bits";
import { type Col, DataGrid } from "../components/DataGrid";
import { View } from "../components/Shell";
import { ViewHeader } from "../components/ViewHeader";
import { TRADES_REFRESH_MS, useLargeTrades } from "../lib/data";
import { Updated } from "../lib/freshness";
import { ago, blankText, count, toneClass, usd } from "../lib/format";
import { matches } from "../lib/markets";
import { openMarket, setParams, useParam, useSearchParam } from "../lib/nav";
import { byMarket, byWallet, type WhaleMarket, type WhaleWallet } from "../lib/whales";
import { SIZE_OPTIONS } from "./WhaleTradesView";

function BuySellBar({ m }: { m: WhaleMarket }) {
	const total = m.buys + m.sells;
	return (
		<span className="flex h-2.5 w-full bg-ink" title={`buys ${usd(m.buys)} · sells ${usd(m.sells)}`}>
			<span className="bg-up/80" style={{ width: `${total ? (m.buys / total) * 100 : 0}%` }} />
			<span className="flex-1 bg-dn/80" />
		</span>
	);
}

const MARKET_COLS: Col<WhaleMarket>[] = [
	{ key: "q", label: "Market", w: 150, grow: 3, sort: (r) => r.title, cell: (r, narrow) => <span className={narrow ? "clamp-2 text-bright" : "text-bright"} title={r.title}>{r.title}</span> },
	{ key: "r", label: "Buy / sell", title: "Share of the loaded large-trade dollars that were buys (green) and sells (red). Sorts by total dollars.", w: 110, grow: 1, sort: (r) => r.buys + r.sells, csv: (r) => `${usd(r.buys)} buys / ${usd(r.sells)} sells`, cell: (r) => <BuySellBar m={r} /> },
	{ key: "n", label: "Net", title: "Buys minus sells in the loaded tape", w: 58, right: true, sort: (r) => r.buys - r.sells, cell: (r) => <span className={toneClass(r.buys - r.sells)}>{usd(r.buys - r.sells)}</span> },
];

const WALLET_COLS: Col<WhaleWallet>[] = [
	{ key: "w", label: "Wallet", w: 140, grow: 2, sort: (r) => r.label ?? r.wallet, csv: (r) => r.wallet, cell: (r) => <WalletLink address={r.wallet} label={r.label} /> },
	{ key: "u", label: "USD traded", title: "Dollars in this wallet's large trades, from the loaded tape only", w: 76, right: true, sort: (r) => r.usd, cell: (r) => <span className="font-bold text-bright">{usd(r.usd)}</span> },
	{ key: "c", label: "Trades", w: 48, right: true, sort: (r) => r.trades, cell: (r) => r.trades },
	{ key: "m", label: "Markets", w: 52, right: true, prio: 1, sort: (r) => r.markets, cell: (r) => r.markets },
	{ key: "l", label: "Largest", title: "The wallet's single largest trade in the loaded tape", w: 58, right: true, prio: 2, sort: (r) => r.largest, cell: (r) => usd(r.largest) },
];

export function WhalesOverviewView() {
	const [minUsd, setMinUsd] = useParam("min", "10000");
	const [mode] = useParam("by", "market");
	const [q, setQ] = useSearchParam();
	// The two grids have different columns, so a sort from one means nothing to the other.
	const setMode = (by: string) => setParams({ by: by === "market" ? null : by, sort: null });
	const { data, error, isLoading, mutate, updatedAt } = useLargeTrades(Number(minUsd));

	const markets = useMemo(() => byMarket(data ?? []), [data]);
	const wallets = useMemo(() => byWallet(data ?? []), [data]);
	const oldest = data?.length ? data[data.length - 1]?.timestamp : null;
	const shownMarkets = useMemo(() => markets.filter((m) => matches(q, m.title)), [markets, q]);
	const shownWallets = useMemo(() => wallets.filter((w) => matches(q, w.label, w.wallet)), [wallets, q]);

	const failed = error && !data ? <SourceError source="Polymarket (data-api)" onRetry={() => void mutate()} /> : undefined;
	const empty = (mode === "market" ? shownMarkets.length : shownWallets.length) === 0;
	const body = failed ?? (data && empty ? <Empty>Nothing matches.</Empty> : undefined);

	return (
		<View>
			<ViewHeader
				title="whales overview"
				meta={data ? `${count(markets.length)} markets · ${count(wallets.length)} wallets` : blankText(25)}
				desc="Where the large trades went: buys against sells per market, and the most active wallets, from the loaded tape."
			>
				<SearchBox value={q} onChange={setQ} placeholder={mode === "market" ? "search markets" : "search wallets"} />
				<Select label="Minimum size" value={minUsd} options={SIZE_OPTIONS} onChange={setMinUsd} />
				<Toggle on={mode === "market"} onClick={() => setMode("market")}>markets</Toggle>
				<Toggle on={mode === "wallet"} onClick={() => setMode("wallet")}>wallets</Toggle>
			</ViewHeader>
			<Panel>
				{mode === "market" ? (
					<DataGrid
						key="market"
						label="Whale flow by market"
						cols={MARKET_COLS}
						rows={shownMarkets}
						rowKey={(r) => r.conditionId}
						initialSort={{ key: "r", dir: "desc" }}
						csvName="whale-flow"
						onRow={(r) => openMarket(r.conditionId)}
						loading={isLoading && !data}
						body={body}
					/>
				) : (
					<DataGrid
						key="wallet"
						label="Most active whale wallets"
						cols={WALLET_COLS}
						rows={shownWallets}
						rowKey={(r) => r.wallet}
						initialSort={{ key: "u", dir: "desc" }}
						csvName="whale-wallets"
						copyValue={(r) => r.wallet}
						loading={isLoading && !data}
						body={body}
					/>
				)}
				<GridNote className="min-h-[41px] sm:min-h-0">
					<Updated at={updatedAt} refreshMs={TRADES_REFRESH_MS} failing={Boolean(error)} />
					{count(data?.length ?? 0)} trades{oldest ? ` over the last ${ago(oldest)}` : ""}, not a full-history total · source: Polymarket data-api
				</GridNote>
			</Panel>
		</View>
	);
}
